import React from "react";
import { Modal, Table, Tag, Avatar, Typography, Divider, Row, Col } from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import round2Decimals from "../../../../Components/round2Decimals";

const { Text, Title } = Typography;

const ModalVerCambios = ({ visible, onCancel, registro, cambios }) => {
  const esCampoMonetario = (campo) => {
    if (!campo) return false;
    const campoLower = campo.toLowerCase();
    return (
      campoLower.includes("precio") ||
      campoLower.includes("total") ||
      campoLower.includes("subtotal") ||
      campoLower.includes("costo") ||
      campoLower.includes("monto") ||
      campoLower.includes("importe")
    );
  };

  const formatearMoneda = (valor) => {
    if (valor === null || valor === undefined || isNaN(valor)) return valor;
    return `${round2Decimals(Number(valor))}`;
  };

  //Funcion para formatear nombres de campos
  const formatearNombreCampo = (texto) => {
    if (!texto) return "";
    const resultado = texto.replace(/([A-Z])/g, " $1");
    return resultado.charAt(0).toUpperCase() + resultado.slice(1);
  };

  // Funcion para limpiar datos: eliminar campos Id y nulos/vacíos
  const limpiarDatos = (obj) => {
    if (!obj || typeof obj !== "object") return obj;

    if (Array.isArray(obj)) {
      const arrayLimpio = obj.map(limpiarDatos).filter((item) => {
        if (typeof item === "object" && item !== null) {
          return Object.keys(item).length > 0;
        }
        return item !== undefined && item !== null;
      });
      return arrayLimpio.length > 0 ? arrayLimpio : undefined;
    }

    const nuevoObj = {};
    Object.keys(obj).forEach((key) => {
      const esId = key.includes("Id") || key === "_id";
      const valorOriginal = obj[key];
      const valorLimpio = limpiarDatos(valorOriginal);

      const esValido =
        valorLimpio !== undefined &&
        valorLimpio !== null &&
        !(
          typeof valorLimpio === "object" &&
          Object.keys(valorLimpio).length === 0
        );

      if (!esId && esValido) {
        nuevoObj[key] = valorLimpio;
      }
    });

    return Object.keys(nuevoObj).length > 0 ? nuevoObj : undefined;
  };

  const VisorDetalle = ({ data }) => {
    if (!data)
      return (
        <Text type="secondary" italic>
          Vacío
        </Text>
      );

    // Caso  Objeto
    if (Array.isArray(data)) {
      if (data.length === 0) return <Text type="secondary">Vacío</Text>;

      return (
        <div style={{ marginTop: 5 }}>
          {data.map((item, idx) => {
            // Crear tabla vertical para cada objeto del array
            const columnsVertical = [
              {
                title: "Campo",
                dataIndex: "campo",
                key: "campo",
                width: "40%",
                render: (text) => (
                  <Text strong style={{ fontSize: 12 }}>
                    {text}
                  </Text>
                ),
              },
              {
                title: "Dato",
                dataIndex: "dato",
                key: "dato",
                render: (val) => {
                  if (typeof val === "object" && val !== null)
                    return <Tag>Detalle</Tag>;
                  return <Text style={{ fontSize: 12 }}>{String(val)}</Text>;
                },
              },
            ];

            // Convertir objeto a formato vertical
            const dataSourceVertical = Object.keys(item).map((key) => ({
              key: key,
              campo: formatearNombreCampo(key),
              dato:
                esCampoMonetario(key) && !isNaN(item[key])
                  ? formatearMoneda(item[key])
                  : item[key],
            }));

            return (
              <div
                key={idx}
                style={{ marginBottom: idx < data.length - 1 ? 15 : 0 }}
              >
                {data.length > 1 && (
                  <Text
                    strong
                    style={{
                      fontSize: 11,
                      color: "#666",
                      display: "block",
                      marginBottom: 5,
                    }}
                  >
                    Registro {idx + 1}
                  </Text>
                )}
                <Table
                  dataSource={dataSourceVertical}
                  columns={columnsVertical}
                  pagination={false}
                  size="small"
                  bordered
                  rowKey="key"
                  showHeader={idx === 0 || data.length === 1}
                />
              </div>
            );
          })}
        </div>
      );
    }

    // Caso Campo simple
    if (typeof data === "object") {
      return (
        <div style={{ paddingLeft: 10, borderLeft: "2px solid #1890ff" }}>
          {Object.keys(data).map((key) => {
            const val = data[key];
            let valorMostrar = val;

            // Formatear si es campo monetario
            if (
              esCampoMonetario(key) &&
              !isNaN(val) &&
              typeof val !== "object"
            ) {
              valorMostrar = formatearMoneda(val);
            }

            return (
              <div key={key} style={{ marginBottom: 4 }}>
                <Text strong style={{ color: "#555", marginRight: 5 }}>
                  {formatearNombreCampo(key)}:
                </Text>
                {typeof val === "object" && val !== null ? (
                  <div style={{ marginLeft: 10, marginTop: 2 }}>
                    <VisorDetalle data={val} />
                  </div>
                ) : (
                  <Text>{String(valorMostrar)}</Text>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return <Text>{String(data)}</Text>;
  };

  const expandedRowRender = (record) => {
    const antLimpio = limpiarDatos(record.anterior);
    const nueLimpio = limpiarDatos(record.nuevo);

    return (
      <div style={{ padding: "10px 20px", backgroundColor: "#fafafa" }}>
        <Row gutter={24}>
          <Col span={12} style={{ borderRight: "1px solid #e8e8e8" }}>
            <Divider
              orientation="left"
              style={{ margin: "0 0 10px 0", color: "#cf1322", fontSize: 12 }}
            >
              VALOR ANTERIOR DETALLADO
            </Divider>
            <VisorDetalle data={antLimpio} />
          </Col>
          <Col span={12}>
            <Divider
              orientation="left"
              style={{ margin: "0 0 10px 0", color: "#389e0d", fontSize: 12 }}
            >
              VALOR NUEVO DETALLADO
            </Divider>
            <VisorDetalle data={nueLimpio} />
          </Col>
        </Row>
      </div>
    );
  };

  const renderCell = (valRaw, isNuevo, campo) => {
    if (valRaw === null || valRaw === undefined) {
      return <Text type="secondary"> Sin registro</Text>;
    }

    if (typeof valRaw === "boolean") {
      return (
        <Tag color={valRaw ? "green" : "red"}>
          {valRaw ? <CheckOutlined /> : <CloseOutlined />}
        </Tag>
      );
    }
    if (
      typeof valRaw === "string" &&
      /^\d{4}-\d{2}-\d{2}T/.test(valRaw) &&
      !isNaN(new Date(valRaw).getTime())
    ) {
      return (
        <Text
          style={{ color: isNuevo ? "#389e0d" : "#595959", fontWeight: 500 }}
        >
          {formatearFecha(valRaw)}
        </Text>
      );
    }
    // Si es objeto, mostramos un indicador para expandir
    if (typeof valRaw === "object") {
      const data = limpiarDatos(valRaw);
      if (!data || (Array.isArray(data) && data.length === 0))
        return <Tag>Vacío</Tag>;

      return (
        <Tag color={isNuevo ? "success" : "default"}>Ver Detalle Abajo</Tag>
      );
    }

    // Si es campo monetario, formatear como moneda
    let valorMostrar = String(valRaw);
    if (esCampoMonetario(campo) && !isNaN(valRaw)) {
      valorMostrar = formatearMoneda(valRaw);
    }

    return (
      <Text style={{ color: isNuevo ? "#389e0d" : "#595959", fontWeight: 500 }}>
        {valorMostrar}
      </Text>
    );
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "";
    const date = new Date(fecha);
    const dia = date.getDate();
    const mes = date.getMonth() + 1;
    const anio = date.getFullYear();
    return `${dia}/${mes}/${anio}`;
  };
  // Detectar si una fila es "de IDs" y no debe mostrarse
  const esFilaDeId = (record) => {
    const campo = (record.campo || "").toLowerCase();

    // 1) Si el nombre del campo trae "id", lo volamos
    if (
      campo.includes(" id") ||
      campo.endsWith("id") ||
      campo.includes("_id")
    ) {
      return true;
    }

    // 2) Si los valores son ObjectId (24 caracteres hex), también la volamos
    const esObjectId = (v) =>
      typeof v === "string" && /^[0-9a-fA-F]{24}$/.test(v);

    if (esObjectId(record.anterior) || esObjectId(record.nuevo)) {
      return true;
    }

    return false;
  };

  // Filtrar cambios para que la tabla no reciba esas filas
  const cambiosFiltrados = (cambios || []).filter((c) => !esFilaDeId(c));

  const columns = [
    {
      title: "Campo Modificado",
      dataIndex: "campo",
      key: "campo",
      width: "25%",
      render: (text) => <Text strong>{formatearNombreCampo(text)}</Text>,
    },
    {
      title: "Valor Anterior",
      dataIndex: "anterior",
      key: "anterior",
      width: "35%",
      render: (val, record) => renderCell(val, false, record.campo),
    },
    {
      title: "Valor Nuevo",
      dataIndex: "nuevo",
      key: "nuevo",
      width: "35%",
      render: (val, record) => renderCell(val, true, record.campo),
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center" }}>
          <FileTextOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          <span>Auditoría de Cambios</span>
        </div>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={1000}
      centered
      bodyStyle={{ padding: "20px 24px" }}
    >
      {registro && (
        <div style={{ marginBottom: 24 }}>
          <Row gutter={16} align="top">
            {" "}
            <Col>
              <Avatar size={50} style={{ backgroundColor: "#1890ff" }}>
                {registro.usuarioNombre ? (
                  registro.usuarioNombre.charAt(0).toUpperCase()
                ) : (
                  <UserOutlined />
                )}
              </Avatar>
            </Col>
            <Col flex="auto">
              <Row gutter={16}>
                <Col xs={24} sm={8} md={6}>
                  <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                    {registro.usuarioNombre}
                  </Title>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block" }}
                  >
                    <CalendarOutlined style={{ marginRight: 5 }} />
                    {registro.fechaLegible ||
                      formatearFecha(registro.createdAt)}
                  </Text>
                </Col>

                <Col xs={24} sm={16} md={18}>
                  <Text
                    strong
                    style={{ fontSize: 13, display: "block", marginBottom: 4 }}
                  >
                    Detalle
                  </Text>
                  <Text style={{ fontSize: 13 }}>{registro.detalles}</Text>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      )}

      <Table
        dataSource={cambiosFiltrados}
        columns={columns}
        pagination={false}
        size="middle"
        bordered
        rowKey={(record) => record.campo}
        scroll={{ y: 500 }}
        expandable={{
          expandedRowRender: expandedRowRender,
          rowExpandable: (record) => {
            return (
              (typeof record.anterior === "object" &&
                record.anterior !== null) ||
              (typeof record.nuevo === "object" && record.nuevo !== null)
            );
          },
          expandRowByClick: true,
        }}
      />
    </Modal>
  );
};

export default ModalVerCambios;
